import { useMemo } from 'react';

import {
  TIMELINE_MAX_THUMBNAILS,
  VERTICAL_OVERRIDE_THUMBNAIL_COUNT,
} from '../utils/constants';
import {
  calculateThumbnails,
  getEffectiveThumbnailsPerSecond,
  ThumbnailMetadata,
  VideoMetadata,
} from '../utils/videoUtils';

interface UseVideoThumbnailsProps {
  videoMetadata: VideoMetadata | null | undefined;
  stripHeight: number;
  containerWidth: number;
  thumbnailsPerSecond?: number;
  thumbnailCountOverride?: number | null;
}

interface UseVideoThumbnailsReturn {
  thumbnails: ThumbnailMetadata[];
  isReady: boolean;
}

export function useVideoThumbnails({
  videoMetadata,
  stripHeight,
  containerWidth,
  thumbnailsPerSecond = 0.5,
}: UseVideoThumbnailsProps): UseVideoThumbnailsReturn {
  const { duration = 0, isVertical = false } = videoMetadata ?? {};

  const effectiveThumbnailsPerSecond = useMemo(() => {
    if (duration <= 0) return 0;
    const thumbnailsPerSecondByVideoType = isVertical
      ? VERTICAL_OVERRIDE_THUMBNAIL_COUNT
      : thumbnailsPerSecond;

    const effectiveThumbnails = getEffectiveThumbnailsPerSecond({
      duration,
      thumbnailsPerSecond: thumbnailsPerSecondByVideoType,
      maxThumbnails: TIMELINE_MAX_THUMBNAILS,
      thumbnailCountOverride: isVertical
        ? VERTICAL_OVERRIDE_THUMBNAIL_COUNT
        : null,
    });
    return effectiveThumbnails;
  }, [duration, thumbnailsPerSecond, isVertical]);

  const thumbnails = useMemo(() => {
    if (
      effectiveThumbnailsPerSecond <= 0 ||
      stripHeight <= 0 ||
      containerWidth <= 0
    ) {
      return [];
    }
    return calculateThumbnails({
      duration,
      stripHeight,
      thumbnailsPerSecond: effectiveThumbnailsPerSecond,
      containerWidth,
      isVertical,
    });
  }, [
    duration,
    stripHeight,
    effectiveThumbnailsPerSecond,
    containerWidth,
    isVertical,
  ]);

  return {
    thumbnails,
    isReady: Boolean(videoMetadata && thumbnails.length > 0),
  };
}
